import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ApprovalStatus, AssetCard } from '@/lib/api/types';
import { api, req } from '@/lib/api/client';
import { useBulkAction, useAddAssetsToBoard, useTags } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { useSelection } from '@/stores/selection';
import { toast } from '@/stores/ui';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { APPROVAL_TONE } from '@/components/Misc';
import { BoardPickerDialog } from '@/features/boards/BoardPickerDialog';
import { SetFieldDialog } from './SetFieldDialog';
import { isApiError } from '@/lib/api/errors';
import { cx } from '@/lib/format';
import { saveUrl } from '@/lib/api/jobs';
import { trackZipJob } from './zipJob';
import { useTidyActions } from '@/features/pins/useTidyActions'; // release1/B
import { usePin, useUnpin, usePins } from '@/lib/api/queries';
import { isPinned } from '@/features/pins/pinsModel';
// --- release3/C ---
import { useDownloadPresets, useExportVersion, type ImageTransform } from '@/lib/api/queries';
import { completeExport } from '@/features/export/runExport';
import { ExportDialog } from '@/features/export/ExportDialog';
import { exportFilename } from '@/features/export/exportModel';

export type AssetActionKind = 'open' | 'move' | 'copy' | 'tag' | 'field' | 'approval' | 'download' | 'delete' | 'remove-from-board' | 'share' | 'archive' | 'unarchive' | 'pin' | 'similar' | 'export'; // release1/B: archive, unarchive, pin; release3/B: similar; release3/C: export (custom dialog)

/** release3/C: `download` takes a preset (single image → export flow; zip → images transformed) or an inline transform. */
export type AssetActionOpts = { status?: ApprovalStatus; card?: AssetCard; presetId?: string | null; transform?: ImageTransform | null };

/**
 * Shared bulk/context actions for grids (board page, search page). Returns `run(kind, ids, opts)`
 * and the dialogs element to mount once.
 */
export function useAssetActions(sourceBoardId: string | null) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const { ws = '' } = useParams();
  const navigate = useNavigate();
  const bulk = useBulkAction(wsId);
  const addAssets = useAddAssetsToBoard();
  const qc = useQueryClient();
  // release1/B — archive with undo toast, pin toggle
  const tidy = useTidyActions();
  const pinsQ = usePins(wsId || undefined);
  const pinM = usePin(wsId);
  const unpinM = useUnpin(wsId);
  // release3/C — warm the presets query here (always mounted with the grid) so the context menu lists presets on its
  // first render: a menu that grows after opening shifts its tail under the pointer (Playwright release1-b caught it).
  const isMember = useSessionStore((s) => s.principal.kind === 'member');
  useDownloadPresets(isMember && wsId ? wsId : undefined);
  const [dialog, setDialog] = useState<{ kind: 'move' | 'copy' | 'tag' | 'field' | 'delete'; ids: string[] } | { kind: 'export'; ids: string[]; card: AssetCard } | null>(null);
  const exportM = useExportVersion(); // release3/C

  const run = (kind: AssetActionKind, ids: string[], opts?: AssetActionOpts) => {
    if (ids.length === 0) return;
    switch (kind) {
      case 'open':
        if (opts?.card) navigate(`/w/${ws}/a/${opts.card.id}`, { state: { background: location.pathname + location.search } });
        return;
      case 'move':
      case 'copy':
      case 'tag':
      case 'field':
      case 'delete':
        setDialog({ kind, ids });
        return;
      case 'approval':
        bulk.mutate({ asset_ids: ids, action: 'set_approval', params: { status: opts?.status ?? 'approved' } }, { onSuccess: () => toast({ kind: 'success', title: `Marked ${ids.length} as ${APPROVAL_TONE[opts?.status ?? 'approved'].label}` }), onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Failed' }) });
        return;
      case 'download':
        if (ids.length === 1 && opts?.card && (opts.presetId || opts.transform)) {
          // --- release3/C --- preset / inline transform: 200 → save the cached rendition, 202 → track the export_render job
          const card = opts.card;
          const name = opts.transform ? exportFilename(card.title, opts.transform) : null;
          exportM.mutate({ versionId: card.active_version_id, presetId: opts.presetId ?? null, transform: opts.transform ?? null }, { onSuccess: (r) => void completeExport(r, { filename: name }), onError: (e) => toast({ kind: 'error', title: isApiError(e) ? (e.code === 'transform_unsupported' ? 'Only images can be exported with a preset' : e.message) : 'Export failed' }) });
        } else if (ids.length === 1 && opts?.card) {
          const card = opts.card;
          void req(api.GET('/versions/{versionId}/download', { params: { path: { versionId: card.active_version_id } } })).then(
            (d) => 'url' in d && d.url && saveUrl(d.url, d.filename || card.title), // release3 (contract): 202 Job when an export is rendering (lane C handles it)
            (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Download failed' }),
          );
        } else {
          bulk.mutate(
            { asset_ids: ids, action: 'download_zip', ...(opts?.presetId ? { params: { preset_id: opts.presetId } } : {}) }, // release3/C: preset applies to image entries
            {
              onSuccess: (r) => {
                const jobId = (r as { job_id?: string | null }).job_id;
                if (!jobId) return toast({ kind: 'error', title: 'The server did not start a zip job' });
                void trackZipJob(() => req(api.GET('/jobs/{jobId}', { params: { path: { jobId } } })), { count: ids.length });
              },
              onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Download failed' }),
            },
          );
        }
        return;
      case 'remove-from-board':
        if (!sourceBoardId) return;
        void Promise.all(ids.map((id) => req(api.DELETE('/boards/{boardId}/assets/{assetId}', { params: { path: { boardId: sourceBoardId, assetId: id } } })))).then(() => {
          void qc.invalidateQueries({ queryKey: ['board-assets', sourceBoardId] });
          useSelection.getState().clear();
        });
        return;
      case 'share':
        navigate(`/w/${ws}/a/${ids[0]}?share=1`, { state: { background: location.pathname + location.search } });
        return;
      // --- release3/B --- find similar: the overlay opens on its Similar strip
      case 'similar':
        navigate(`/w/${ws}/a/${ids[0]}?similar=1`, { state: { background: location.pathname + location.search } });
        return;
      // --- release1/B ---
      case 'archive':
      case 'unarchive':
        tidy.archiveAssets(ids, kind === 'archive');
        return;
      case 'pin': {
        const id = ids[0]!;
        if (isPinned(pinsQ.data, 'asset', id)) unpinM.mutate({ kind: 'asset', targetId: id });
        else pinM.mutate({ kind: 'asset', targetId: id }, { onSuccess: () => toast({ kind: 'success', title: 'Pinned to sidebar' }), onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not pin' }) });
        return;
      }
      // --- release3/C ---
      case 'export':
        if (opts?.card) setDialog({ kind: 'export', ids, card: opts.card });
        return;
    }
  };

  const dialogs: ReactNode = (
    <>
      {(dialog?.kind === 'move' || dialog?.kind === 'copy') && (
        <BoardPickerDialog
          title={dialog.kind === 'move' ? `Move ${dialog.ids.length} to…` : `Copy ${dialog.ids.length} to…`}
          confirmLabel={dialog.kind === 'move' ? 'Move' : 'Copy'}
          exclude={sourceBoardId ? [sourceBoardId] : []}
          onClose={() => setDialog(null)}
          onPick={(t) => {
            if (!t.boardId) return;
            if (dialog.kind === 'move') bulk.mutate({ asset_ids: dialog.ids, action: 'move_to_board', params: { board_id: t.boardId, from_board_id: sourceBoardId } }, { onSuccess: () => (toast({ kind: 'success', title: `Moved ${dialog.ids.length}` }), useSelection.getState().clear()) });
            else addAssets.mutate({ boardId: t.boardId, assetIds: dialog.ids, fromBoardId: sourceBoardId, mode: 'copy' }, { onSuccess: () => toast({ kind: 'success', title: `Copied ${dialog.ids.length}` }) });
            setDialog(null);
          }}
        />
      )}
      {dialog?.kind === 'tag' && <TagDialog ids={dialog.ids} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'export' && <ExportDialog versionId={dialog.card.active_version_id} filename={dialog.card.title} onClose={() => setDialog(null)} /> /* release3/C */}
      {dialog?.kind === 'field' && <SetFieldDialog ids={dialog.ids} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'delete' && (
        <Dialog
          open
          onClose={() => setDialog(null)}
          title="Delete assets"
          footer={
            <>
              <Button onClick={() => setDialog(null)}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => {
                  bulk.mutate({ asset_ids: dialog.ids, action: 'delete' }, { onSuccess: () => (toast({ kind: 'success', title: `Deleted ${dialog.ids.length}` }), useSelection.getState().clear()) });
                  setDialog(null);
                }}
              >
                Delete
              </Button>
            </>
          }
        >
          <p>Move {dialog.ids.length === 1 ? 'this asset' : `${dialog.ids.length} assets`} to the trash? They can be restored for 30 days.</p>
        </Dialog>
      )}
    </>
  );
  return { run, dialogs };
}

function TagDialog({ ids, onClose }: { ids: string[]; onClose: () => void }) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const tags = useTags(wsId);
  const bulk = useBulkAction(wsId);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const list = (tags.data ?? []).filter((t) => t.name.includes(q.toLowerCase()));
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${mode === 'add' ? 'Add' : 'Remove'} tags (${ids.length})`}
      footer={
        <>
          <Button onClick={() => setMode((m) => (m === 'add' ? 'remove' : 'add'))}>{mode === 'add' ? 'Switch to remove' : 'Switch to add'}</Button>
          <Button
            variant="primary"
            disabled={picked.size === 0}
            onClick={() => {
              bulk.mutate({ asset_ids: ids, action: mode === 'add' ? 'add_tags' : 'remove_tags', params: { tag_ids: [...picked] } }, { onSuccess: () => toast({ kind: 'success', title: `${mode === 'add' ? 'Tagged' : 'Untagged'} ${ids.length}` }) });
              onClose();
            }}
          >
            Apply
          </Button>
        </>
      }
    >
      <Input placeholder="Filter tags…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus className="mb-2" />
      <div className="flex max-h-64 flex-wrap gap-1.5 overflow-y-auto">
        {list.map((t) => (
          <button key={t.id} type="button" aria-pressed={picked.has(t.id)} onClick={() => setPicked((p) => { const n = new Set(p); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n; })} className={cx('rounded-full border px-2.5 py-0.5 text-xs', picked.has(t.id) ? 'border-accent bg-accent-soft text-accent' : 'border-line hover:bg-bg-sunken')}>
            {t.name}
            {t.asset_count != null && <span className="ml-1 text-fg-subtle">{t.asset_count}</span>}
          </button>
        ))}
      </div>
    </Dialog>
  );
}
